import React, { useState, useEffect } from "react";
import { useParams, useNavigate, Link } from "react-router-dom";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import { getAnalysis, fetchExport, API } from "@/lib/api";
import { generateOnboarding, getOnboardingStatus } from "@/lib/phase3_api";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "@/components/ui/tabs";
import { Copy, DownloadSimple, FileText, ArrowLeft, FilePdf, Printer, RocketLaunch, Path, Lightbulb, Warning, Star } from "@phosphor-icons/react";
import { toast } from "sonner";
import { ObservedBadge, ConfidenceBadge, PriorityBadge } from "@/components/Badges";
import { CompetitorsSection, ReviewsSection, DiffSection, RICECell, ScreenshotStrip } from "@/components/Phase2Sections";
import { WalkthroughBlock } from "@/components/Walkthrough";
import { VerifyAllPanel } from "@/components/VerifyScorecard";

function copyText(text) {
  return new Promise((resolve, reject) => {
    const fallback = () => {
      try {
        const ta = document.createElement("textarea");
        ta.value = text;
        ta.style.position = "fixed";
        ta.style.left = "-9999px";
        document.body.appendChild(ta);
        ta.select();
        const ok = document.execCommand("copy");
        document.body.removeChild(ta);
        if (ok) resolve();
        else reject(new Error("copy command rejected"));
      } catch (e) {
        reject(e);
      }
    };
    if (navigator.clipboard && window.isSecureContext) {
      navigator.clipboard.writeText(text).then(resolve).catch(fallback);
    } else {
      fallback();
    }
  });
}

function downloadFile(name, content, mime = "text/markdown") {
  const blob = new Blob([content], { type: mime });
  const url = URL.createObjectURL(blob);
  const a = document.createElement("a");
  a.href = url;
  a.download = name;
  a.click();
  URL.revokeObjectURL(url);
}

function Section({ index, title, children, testid }) {
  return (
    <section data-testid={testid} className="border border-zinc-200 bg-white mb-6">
      <header className="px-6 py-4 border-b border-zinc-200 flex items-baseline gap-3">
        <span className="font-mono text-[10px] uppercase tracking-widest text-zinc-500">
          §{index}
        </span>
        <h2 className="font-display text-xl font-semibold tracking-tight text-zinc-950">
          {title}
        </h2>
      </header>
      <div className="p-6">{children}</div>
    </section>
  );
}

function KV({ label, children }) {
  if (!children) return null;
  return (
    <div className="grid grid-cols-[180px_1fr] gap-4 py-2 border-b border-zinc-100 last:border-b-0">
      <div className="font-mono text-[10px] uppercase tracking-widest text-zinc-500 pt-1">
        {label}
      </div>
      <div className="text-sm text-zinc-800 leading-relaxed">{children}</div>
    </div>
  );
}

function BusinessOverview({ bp }) {
  if (!bp) return <div className="text-sm text-zinc-500">No business profile available.</div>;
  return (
    <div>
      {bp.one_line_pitch && (
        <div className="font-display text-xl text-zinc-950 mb-4 leading-snug">
          {bp.one_line_pitch}
        </div>
      )}
      {bp.description && (
        <p className="text-zinc-700 leading-relaxed mb-6">{bp.description}</p>
      )}
      <div className="border-t border-zinc-200 pt-2">
        <KV label="Vertical">
          <div className="flex items-center gap-2">
            <span className="font-medium" data-testid="business-vertical">{bp.vertical || "—"}</span>
            <ConfidenceBadge level={bp.vertical_confidence} />
          </div>
        </KV>
        <KV label="Value proposition">{bp.value_proposition}</KV>
        <KV label="ICP">
          <div className="flex flex-wrap gap-1.5">
            {(bp.icp || []).map((s, i) => (
              <span key={i} className="border border-zinc-300 px-2 py-0.5 font-mono text-[11px]">
                {s}
              </span>
            ))}
            {(!bp.icp || bp.icp.length === 0) && <span className="text-zinc-500">—</span>}
          </div>
        </KV>
        <KV label="Pricing model">{bp.pricing_model}</KV>
        <KV label="Stage">
          {bp.company_stage}
          {bp.stage_signals?.length > 0 && (
            <ul className="mt-1 space-y-1 text-xs text-zinc-600 list-disc pl-5">
              {bp.stage_signals.map((s, i) => <li key={i}>{s}</li>)}
            </ul>
          )}
        </KV>
        <KV label="Positioning">{bp.positioning}</KV>
        <KV label="Monetisation">{bp.monetisation_notes}</KV>
      </div>

      {bp.pricing_tiers?.length > 0 && (
        <div className="mt-6">
          <div className="font-mono text-[10px] uppercase tracking-widest text-zinc-500 mb-2">
            Pricing tiers
          </div>
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-px bg-zinc-200 border border-zinc-200">
            {bp.pricing_tiers.map((t, i) => (
              <div key={i} className="bg-white p-4">
                <div className="font-display font-semibold text-zinc-950">{t.name}</div>
                <div className="font-mono text-sm text-zinc-700 mt-1">
                  {t.price || "—"} {t.cadence ? <span className="text-zinc-500">/ {t.cadence}</span> : null}
                </div>
                {t.target && <div className="text-xs text-zinc-600 mt-2">For: {t.target}</div>}
                {t.notable_features?.length > 0 && (
                  <ul className="mt-2 space-y-1 text-xs text-zinc-700 list-disc pl-4">
                    {t.notable_features.map((f, j) => <li key={j}>{f}</li>)}
                  </ul>
                )}
              </div>
            ))}
          </div>
        </div>
      )}
    </div>
  );
}

function SystemMap({ tp }) {
  if (!tp) return <div className="text-sm text-zinc-500">No tech profile available.</div>;
  const byCat = (tp.items || []).reduce((acc, it) => {
    (acc[it.category] = acc[it.category] || []).push(it);
    return acc;
  }, {});
  const cats = Object.keys(byCat).sort();

  return (
    <div>
      <p className="text-sm text-zinc-700 leading-relaxed mb-6 border-l-2 border-zinc-950 pl-3">
        Backend internals are not visible from outside. Items below are detected from headers,
        cookies, scripts and DNS, and every item is tagged with whether it was directly{" "}
        <strong>observed</strong> on the public surface or <strong>inferred</strong>, plus a
        confidence level. We never present a guess as a fact.
      </p>

      {cats.length === 0 && (
        <div className="text-sm text-zinc-500">No technologies confidently detected.</div>
      )}

      {cats.map((cat) => (
        <div key={cat} className="mb-5">
          <div className="font-mono text-[10px] uppercase tracking-widest text-zinc-500 mb-2">
            {cat.replace(/_/g, " ")}
          </div>
          <div className="border border-zinc-200">
            {byCat[cat].map((item, idx, arr) => (
              <div
                key={item.name}
                className={`px-4 py-3 ${idx !== arr.length - 1 ? "border-b border-zinc-200" : ""}`}
                data-testid={`tech-item-${item.name}`}
              >
                <div className="flex items-center gap-2 flex-wrap">
                  <span className="font-display font-medium text-zinc-950">{item.name}</span>
                  <ObservedBadge observed={item.observed} />
                  <ConfidenceBadge level={item.confidence} />
                </div>
                {item.evidence?.length > 0 && (
                  <div className="mt-1.5 font-mono text-[11px] text-zinc-500 space-y-0.5">
                    {item.evidence.slice(0, 4).map((e, i) => (
                      <div key={i}>↳ {e}</div>
                    ))}
                  </div>
                )}
              </div>
            ))}
          </div>
        </div>
      ))}

      {tp.inferred_backend_note && (
        <div className="mt-6 bg-zinc-50 border border-dashed border-zinc-400 p-4 text-sm text-zinc-700 leading-relaxed">
          <div className="font-mono text-[10px] uppercase tracking-widest text-zinc-500 mb-1">
            Note on inferred items
          </div>
          {tp.inferred_backend_note}
        </div>
      )}
    </div>
  );
}

function FeatureInventory({ fi }) {
  const items = fi?.items || [];
  if (items.length === 0) {
    return <div className="text-sm text-zinc-500">No features extracted.</div>;
  }
  return (
    <div className="grid grid-cols-1 md:grid-cols-2 gap-px bg-zinc-200 border border-zinc-200">
      {items.map((f, i) => (
        <div key={i} className="bg-white p-4" data-testid={`feature-item-${i}`}>
          <div className="font-display font-medium text-zinc-950">{f.name}</div>
          {f.description && <div className="text-sm text-zinc-700 mt-1 leading-relaxed">{f.description}</div>}
          {f.source && (
            <div className="mt-2 font-mono text-[10px] uppercase tracking-widest text-zinc-500">
              source · {f.source}
            </div>
          )}
        </div>
      ))}
    </div>
  );
}

function IndustryLensView({ lens }) {
  if (!lens) return <div className="text-sm text-zinc-500">No lens applied.</div>;
  return (
    <div>
      <div className="font-display text-lg font-semibold text-zinc-950 mb-4" data-testid="lens-vertical">
        {lens.vertical}
      </div>
      <div className="grid grid-cols-1 md:grid-cols-2 gap-px bg-zinc-200 border border-zinc-200">
        {[
          ["Buyer concerns", lens.buyer_concerns],
          ["Compliance / regulatory", lens.compliance],
          ["Expected integrations", lens.integration_expectations],
          ["Churn drivers", lens.churn_drivers],
          ["Anti-patterns to avoid", lens.anti_patterns],
        ].map(([label, arr]) => (
          <div key={label} className="bg-white p-4">
            <div className="font-mono text-[10px] uppercase tracking-widest text-zinc-500 mb-2">
              {label}
            </div>
            <ul className="space-y-1 text-sm text-zinc-800 list-disc pl-5">
              {(arr || []).map((s, i) => <li key={i}>{s}</li>)}
              {(!arr || arr.length === 0) && <li className="text-zinc-500 list-none -ml-5">—</li>}
            </ul>
          </div>
        ))}
      </div>
      {lens.security_data_sensitivity && (
        <div className="mt-4 bg-zinc-50 border border-zinc-200 p-4 text-sm text-zinc-700">
          <div className="font-mono text-[10px] uppercase tracking-widest text-zinc-500 mb-1">
            Security / data sensitivity
          </div>
          {lens.security_data_sensitivity}
        </div>
      )}
    </div>
  );
}

function BuildSpecView({ bs, analysisId, competitors = [], onItemUpdated }) {
  const items = bs?.items || [];
  if (items.length === 0) {
    return <div className="text-sm text-zinc-500">No build items synthesised.</div>;
  }
  return (
    <div>
      <VerifyAllPanel analysisId={analysisId} itemCount={items.length} competitors={competitors} />
      {bs.summary && (
        <div className="mb-6 border-l-2 border-zinc-950 pl-3 text-zinc-700 leading-relaxed">
          {bs.summary}
        </div>
      )}
      <div className="space-y-4">
        {items.map((it, idx) => (
          <article
            key={it.id || idx}
            data-testid={`build-item-${idx}`}
            className="border border-zinc-200 bg-white"
          >
            <header className="px-5 py-3 border-b border-zinc-200 flex flex-wrap items-center gap-3 bg-zinc-50">
              <span className="font-mono text-xs text-zinc-500">#{String(idx + 1).padStart(2, "0")}</span>
              <h3 className="font-display font-semibold text-zinc-950 text-lg flex-1 min-w-[200px]">
                {it.title}
              </h3>
              <PriorityBadge priority={it.priority} />
              <ConfidenceBadge level={it.confidence} />
            </header>
            <div className="p-5">
              <p className="text-zinc-800 leading-relaxed mb-4">{it.description}</p>

              <div className="grid grid-cols-1 md:grid-cols-2 gap-4 text-sm">
                {it.rationale && (
                  <div>
                    <div className="font-mono text-[10px] uppercase tracking-widest text-zinc-500 mb-1">
                      Why
                    </div>
                    <div className="text-zinc-800">{it.rationale}</div>
                  </div>
                )}
                {it.industry_fit_note && (
                  <div>
                    <div className="font-mono text-[10px] uppercase tracking-widest text-zinc-500 mb-1">
                      Industry fit
                    </div>
                    <div className="text-zinc-800">{it.industry_fit_note}</div>
                  </div>
                )}
                {it.audience && (
                  <div>
                    <div className="font-mono text-[10px] uppercase tracking-widest text-zinc-500 mb-1">
                      Audience
                    </div>
                    <div className="text-zinc-800">{it.audience}</div>
                  </div>
                )}
                {it.build_vs_buy && (
                  <div>
                    <div className="font-mono text-[10px] uppercase tracking-widest text-zinc-500 mb-1">
                      Build vs buy
                    </div>
                    <div className="text-zinc-800">{it.build_vs_buy}</div>
                  </div>
                )}
              </div>

              {it.evidence_refs?.length > 0 && (
                <div className="mt-4 border-t border-zinc-100 pt-4">
                  <div className="font-mono text-[10px] uppercase tracking-widest text-zinc-500 mb-2">
                    Evidence
                  </div>
                  <ul className="space-y-1">
                    {it.evidence_refs.map((e, i) => (
                      <li key={i} className="font-mono text-xs text-zinc-700">
                        ↳ {e}
                      </li>
                    ))}
                  </ul>
                </div>
              )}

              <RICECell item={it} />

              <WalkthroughBlock
                analysisId={analysisId}
                item={it}
                competitors={competitors}
                onUpdated={(wt) => onItemUpdated && onItemUpdated(idx, wt)}
              />

              <div className="mt-4 pt-3 border-t border-zinc-100 flex flex-wrap gap-4 font-mono text-[11px] uppercase tracking-widest text-zinc-500">
                <span>impact · <span className="text-zinc-800">{it.impact}</span></span>
                <span>effort · <span className="text-zinc-800">{it.effort}</span></span>
                <span>priority · <span className="text-zinc-800">{it.priority}</span></span>
                <span>confidence · <span className="text-zinc-800">{it.confidence}</span></span>
              </div>
            </div>
          </article>
        ))}
      </div>
    </div>
  );
}

function ExportPanel({ id }) {
  const [busy, setBusy] = useState(null);

  const handle = async (kind, action) => {
    try {
      setBusy(`${kind}-${action}`);
      const txt = await fetchExport(id, kind);
      if (action === "copy") {
        await copyText(txt);
        toast.success(`${kind === "markdown" ? "Markdown" : "Feature spec"} copied`);
      } else {
        const fname = kind === "markdown" ? `teardown-${id.slice(0, 8)}.md` : `feature-spec-${id.slice(0, 8)}.md`;
        downloadFile(fname, txt);
        toast.success("Download started");
      }
    } catch (e) {
      toast.error(`Export failed: ${e.message}`);
    } finally {
      setBusy(null);
    }
  };

  const handlePdf = () => {
    // Direct browser download
    window.open(`${API}/analyses/${id}/export/pdf`, "_blank");
    toast.success("PDF opening in a new tab");
  };

  const handlePrint = () => {
    window.open(`/analysis/${id}/print`, "_blank");
  };

  const handleSendToEmergent = async () => {
    try {
      setBusy("send-emergent");
      const spec = await fetchExport(id, "feature-spec");
      await copyText(spec);
      window.open("https://app.emergent.sh", "_blank");
      toast.success("Feature spec copied — paste it into Emergent to start a build");
    } catch (e) {
      toast.error(`Copy failed: ${e.message}`);
    } finally {
      setBusy(null);
    }
  };

  return (
    <div className="border border-zinc-200 bg-white" data-testid="export-panel">
      <div className="px-5 py-3 border-b border-zinc-200 font-mono text-[10px] uppercase tracking-widest text-zinc-500 bg-zinc-50 flex items-center gap-2">
        <FileText size={12} /> Export
      </div>
      <div className="p-4 grid grid-cols-2 gap-2">
        <button
          data-testid="copy-markdown-button"
          onClick={() => handle("markdown", "copy")}
          disabled={!!busy}
          className="border border-zinc-300 px-3 py-2 font-mono text-[11px] uppercase tracking-widest text-zinc-800 hover:bg-zinc-100 transition-colors flex items-center justify-center gap-2 disabled:opacity-60"
        >
          <Copy size={12} /> Copy MD
        </button>
        <button
          data-testid="download-markdown-button"
          onClick={() => handle("markdown", "download")}
          disabled={!!busy}
          className="border border-zinc-300 px-3 py-2 font-mono text-[11px] uppercase tracking-widest text-zinc-800 hover:bg-zinc-100 transition-colors flex items-center justify-center gap-2 disabled:opacity-60"
        >
          <DownloadSimple size={12} /> Download MD
        </button>
        <button
          data-testid="copy-spec-button"
          onClick={() => handle("feature-spec", "copy")}
          disabled={!!busy}
          className="bg-zinc-950 text-white px-3 py-2 font-mono text-[11px] uppercase tracking-widest hover:bg-zinc-800 transition-colors flex items-center justify-center gap-2 disabled:opacity-60"
        >
          <Copy size={12} /> Copy spec
        </button>
        <button
          data-testid="download-spec-button"
          onClick={() => handle("feature-spec", "download")}
          disabled={!!busy}
          className="border border-zinc-950 text-zinc-950 px-3 py-2 font-mono text-[11px] uppercase tracking-widest hover:bg-zinc-100 transition-colors flex items-center justify-center gap-2 disabled:opacity-60"
        >
          <DownloadSimple size={12} /> Download spec
        </button>
        <button
          data-testid="download-pdf-button"
          onClick={handlePdf}
          className="col-span-2 border border-zinc-300 px-3 py-2 font-mono text-[11px] uppercase tracking-widest text-zinc-800 hover:bg-zinc-100 transition-colors flex items-center justify-center gap-2"
        >
          <FilePdf size={12} /> Download PDF
        </button>
        <button
          data-testid="print-button"
          onClick={handlePrint}
          className="col-span-2 border border-zinc-300 px-3 py-2 font-mono text-[11px] uppercase tracking-widest text-zinc-800 hover:bg-zinc-100 transition-colors flex items-center justify-center gap-2"
        >
          <Printer size={12} /> Print view
        </button>
        <button
          data-testid="send-to-emergent-button"
          onClick={handleSendToEmergent}
          disabled={!!busy}
          className="col-span-2 bg-emerald-600 text-white px-3 py-2 font-mono text-[11px] uppercase tracking-widest hover:bg-emerald-700 transition-colors flex items-center justify-center gap-2 disabled:opacity-60"
        >
          <RocketLaunch size={12} weight="fill" /> {busy === "send-emergent" ? "Preparing…" : "Send to Emergent"}
        </button>
      </div>
      <div className="px-4 pb-4 font-mono text-[10px] text-zinc-500 leading-relaxed">
        Feature spec is builder-ready Markdown — paste directly into an AI builder prompt.
      </div>
    </div>
  );
}

function OnboardingTab({ a, onDone }) {
  const ow = a.onboarding_walkthrough;
  const [enqueuing, setEnqueuing] = useState(false);
  const initialInFlight = a.onboarding_status === "queued" || a.onboarding_status === "generating";
  const [polling, setPolling] = useState(initialInFlight);

  const { data: st } = useQuery({
    queryKey: ["onboarding-status", a.id],
    queryFn: () => getOnboardingStatus(a.id),
    enabled: polling,
    refetchInterval: polling ? 3000 : false,
  });

  const status = (polling && st?.status) || a.onboarding_status || "idle";
  const inFlight = status === "queued" || status === "generating";
  const error = st?.error || a.onboarding_error || "";

  useEffect(() => {
    if (!polling || !st) return;
    if (st.status === "complete") {
      setPolling(false);
      toast.success("Onboarding walkthrough generated");
      onDone && onDone();
    } else if (st.status === "failed") {
      setPolling(false);
      toast.error(st.error || "Onboarding generation failed");
      onDone && onDone();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [st?.status, polling]);

  const run = async () => {
    try {
      setEnqueuing(true);
      await generateOnboarding(a.id);
      setPolling(true);
      toast.info("Queued — reconstructing the journey in the background");
    } catch (e) {
      toast.error(e.message || "Could not queue onboarding walkthrough");
    } finally {
      setEnqueuing(false);
    }
  };

  const Progress = (
    <div
      className="border border-emerald-300 bg-emerald-50 p-4 flex items-start gap-3"
      data-testid="onboarding-progress"
    >
      <span className="mt-0.5 h-3 w-3 shrink-0 rounded-full bg-emerald-600 animate-pulse" />
      <div>
        <div className="font-mono text-[10px] uppercase tracking-widest text-emerald-800 mb-1">
          {status === "queued" ? "Queued" : "Reconstructing"}
        </div>
        <div className="text-sm text-emerald-900">
          {status === "queued"
            ? "Waiting for a worker slot — this keeps running even if you close the tab."
            : "Walking the crawled surfaces and mapping the activation journey. Usually 20–60 seconds."}
        </div>
      </div>
    </div>
  );

  if (!ow) {
    return (
      <div data-testid="onboarding-empty">
        {inFlight ? (
          Progress
        ) : (
          <div className="text-center py-10">
            <Path size={28} className="mx-auto text-zinc-400 mb-3" />
            <div className="font-display text-lg font-semibold text-zinc-950 mb-1">
              Reverse-engineer the onboarding
            </div>
            <p className="text-sm text-zinc-600 max-w-md mx-auto mb-5 leading-relaxed">
              Reconstruct the activation journey a new user walks through — step by step, mapped to the
              pages we crawled — so you can copy the good parts. Public surface only; we never log in.
            </p>
            {status === "failed" && error && (
              <div
                className="max-w-md mx-auto mb-4 border border-red-300 bg-red-50 p-3 text-sm text-red-800"
                data-testid="onboarding-error"
              >
                {error}
              </div>
            )}
            <button
              onClick={run}
              disabled={enqueuing}
              data-testid="generate-onboarding-button"
              className="bg-emerald-600 text-white px-5 py-2.5 font-mono text-xs uppercase tracking-widest hover:bg-emerald-700 transition-colors disabled:opacity-60 inline-flex items-center gap-2"
            >
              {enqueuing ? "Queueing…" : status === "failed" ? "Retry walkthrough" : "Generate onboarding walkthrough"}
              <Path size={14} weight="bold" />
            </button>
          </div>
        )}
      </div>
    );
  }

  return (
    <div data-testid="onboarding-walkthrough">
      {inFlight && <div className="mb-4">{Progress}</div>}
      {status === "failed" && error && (
        <div className="mb-4 border border-red-300 bg-red-50 p-3 text-sm text-red-800" data-testid="onboarding-error">
          {error}
        </div>
      )}
      {ow.overview && (
        <p className="text-zinc-700 leading-relaxed mb-4 border-l-2 border-zinc-950 pl-3">{ow.overview}</p>
      )}
      {ow.activation_insight && (
        <div className="mb-6 bg-emerald-50 border border-emerald-300 p-4" data-testid="onboarding-activation">
          <div className="font-mono text-[10px] uppercase tracking-widest text-emerald-800 mb-1 flex items-center gap-1.5">
            <Lightbulb size={12} weight="fill" /> Activation moment
          </div>
          <div className="text-sm text-emerald-900">{ow.activation_insight}</div>
        </div>
      )}

      <div className="space-y-4">
        {ow.steps.map((s, i) => (
          <article key={i} data-testid={`onboarding-step-${i}`} className="border border-zinc-200 bg-white">
            <header className="px-5 py-3 border-b border-zinc-200 flex items-center gap-3 bg-zinc-50">
              <span className="font-mono text-xs text-zinc-500">{String(s.order || i + 1).padStart(2, "0")}</span>
              <h3 className="font-display font-semibold text-zinc-950 flex-1">{s.title}</h3>
              {s.surface_type && (
                <span className="font-mono text-[10px] uppercase tracking-widest text-zinc-500">{s.surface_type}</span>
              )}
            </header>
            <div className="grid grid-cols-1 md:grid-cols-[200px_1fr]">
              <div className="border-b md:border-b-0 md:border-r border-zinc-200 bg-zinc-50 p-3">
                {s.screenshot_path ? (
                  <a href={`${API}/screenshots/${s.screenshot_path}`} target="_blank" rel="noreferrer">
                    <img
                      src={`${API}/screenshots/${s.screenshot_path}`}
                      alt={s.title}
                      className="w-full h-auto border border-zinc-300 object-cover object-top max-h-44"
                      data-testid={`onboarding-shot-${i}`}
                    />
                  </a>
                ) : (
                  <div className="h-28 flex items-center justify-center border border-dashed border-zinc-300 text-zinc-400 font-mono text-[10px] uppercase tracking-widest text-center px-2">
                    No screenshot
                  </div>
                )}
                {s.surface_url && (
                  <a href={s.surface_url} target="_blank" rel="noreferrer" className="mt-2 block font-mono text-[10px] text-zinc-500 hover:underline break-all">
                    {s.surface_url}
                  </a>
                )}
              </div>
              <div className="p-4 space-y-3">
                {s.what_happens && <p className="text-sm text-zinc-800 leading-relaxed">{s.what_happens}</p>}
                {s.why_it_works && (
                  <div>
                    <div className="font-mono text-[10px] uppercase tracking-widest text-zinc-500 mb-0.5">Why it works</div>
                    <div className="text-sm text-zinc-700">{s.why_it_works}</div>
                  </div>
                )}
                {s.copy_worthy && (
                  <div className="bg-amber-50 border border-amber-200 p-3">
                    <div className="font-mono text-[10px] uppercase tracking-widest text-amber-800 mb-0.5 flex items-center gap-1.5">
                      <Star size={11} weight="fill" /> Steal this
                    </div>
                    <div className="text-sm text-amber-900">{s.copy_worthy}</div>
                  </div>
                )}
              </div>
            </div>
          </article>
        ))}
      </div>

      {ow.friction_points?.length > 0 && (
        <div className="mt-6">
          <div className="font-mono text-[10px] uppercase tracking-widest text-zinc-500 mb-2 flex items-center gap-1.5">
            <Warning size={12} weight="fill" className="text-amber-600" /> Friction points
          </div>
          <ul className="space-y-1.5">
            {ow.friction_points.map((f, i) => (
              <li key={i} className="text-sm text-zinc-700 flex items-start gap-2">
                <Warning size={14} weight="fill" className="text-amber-600 mt-0.5 shrink-0" /> {f}
              </li>
            ))}
          </ul>
        </div>
      )}

      {ow.note && <div className="mt-5 font-mono text-[11px] text-zinc-500 border-t border-zinc-100 pt-3">{ow.note}</div>}

      <button
        onClick={run}
        disabled={enqueuing || inFlight}
        data-testid="regenerate-onboarding-button"
        className="mt-6 border border-zinc-300 px-4 py-2 font-mono text-[11px] uppercase tracking-widest text-zinc-700 hover:bg-zinc-100 transition-colors disabled:opacity-60"
      >
        {inFlight ? "Regenerating…" : enqueuing ? "Queueing…" : "Regenerate"}
      </button>
    </div>
  );
}

export default function Report() {
  const { id } = useParams();
  const navigate = useNavigate();
  const qc = useQueryClient();
  const { data: a, isLoading } = useQuery({
    queryKey: ["analysis", id],
    queryFn: () => getAnalysis(id),
  });

  if (isLoading || !a) {
    return (
      <div className="flex-1 p-12">
        <div className="font-mono text-xs text-zinc-500">Loading report…</div>
      </div>
    );
  }

  if (a.status !== "complete") {
    // Redirect-like message
    return (
      <div className="flex-1 p-12">
        <div className="font-mono text-xs text-zinc-500 mb-3">Status: {a.status}</div>
        <button
          onClick={() => navigate(`/analysis/${id}`)}
          className="bg-zinc-950 text-white px-4 py-2 font-mono text-xs uppercase tracking-widest hover:bg-zinc-800"
          data-testid="back-to-progress-button"
        >
          Back to pipeline
        </button>
      </div>
    );
  }

  return (
    <div className="flex-1 min-h-screen flex flex-col" data-testid="report-page">
      <header className="border-b border-zinc-200 bg-white px-6 lg:px-12 py-6">
        <Link to="/" className="inline-flex items-center gap-1.5 font-mono text-[11px] uppercase tracking-widest text-zinc-500 hover:text-zinc-900 mb-2" data-testid="back-link">
          <ArrowLeft size={12} /> New analysis
        </Link>
        <div className="flex flex-wrap items-baseline gap-3">
          <div className="font-mono text-[11px] uppercase tracking-widest text-zinc-500">Report</div>
          <h1 className="font-display text-2xl sm:text-3xl font-semibold tracking-tight text-zinc-950" data-testid="report-target-url">
            {a.target_url}
          </h1>
        </div>
        {a.business_profile?.vertical && (
          <div className="mt-1 font-mono text-xs text-zinc-600">
            {a.business_profile.vertical}
            {a.business_profile.one_line_pitch && <> · {a.business_profile.one_line_pitch}</>}
          </div>
        )}
      </header>

      <div className="flex-1 grid grid-cols-1 lg:grid-cols-[1fr_320px] gap-6 px-6 lg:px-12 py-8">
        <div>
          <Tabs defaultValue="overview" className="w-full" data-testid="report-tabs">
            <TabsList className="sharp bg-zinc-100 border border-zinc-200 p-0 h-auto flex-wrap">
              <TabsTrigger value="overview" data-testid="tab-overview" className="sharp data-[state=active]:bg-white data-[state=active]:border-zinc-950 border border-transparent font-mono text-xs uppercase tracking-widest px-4 py-2">Business</TabsTrigger>
              <TabsTrigger value="system" data-testid="tab-system" className="sharp data-[state=active]:bg-white data-[state=active]:border-zinc-950 border border-transparent font-mono text-xs uppercase tracking-widest px-4 py-2">System map</TabsTrigger>
              <TabsTrigger value="features" data-testid="tab-features" className="sharp data-[state=active]:bg-white data-[state=active]:border-zinc-950 border border-transparent font-mono text-xs uppercase tracking-widest px-4 py-2">Features</TabsTrigger>
              <TabsTrigger value="lens" data-testid="tab-lens" className="sharp data-[state=active]:bg-white data-[state=active]:border-zinc-950 border border-transparent font-mono text-xs uppercase tracking-widest px-4 py-2">Industry lens</TabsTrigger>
              <TabsTrigger value="competitors" data-testid="tab-competitors" className="sharp data-[state=active]:bg-white data-[state=active]:border-zinc-950 border border-transparent font-mono text-xs uppercase tracking-widest px-4 py-2">Competitors</TabsTrigger>
              <TabsTrigger value="reviews" data-testid="tab-reviews" className="sharp data-[state=active]:bg-white data-[state=active]:border-zinc-950 border border-transparent font-mono text-xs uppercase tracking-widest px-4 py-2">Reviews</TabsTrigger>
              <TabsTrigger value="spec" data-testid="tab-spec" className="sharp data-[state=active]:bg-white data-[state=active]:border-zinc-950 border border-transparent font-mono text-xs uppercase tracking-widest px-4 py-2">Build spec</TabsTrigger>
              <TabsTrigger value="diff" data-testid="tab-diff" className="sharp data-[state=active]:bg-white data-[state=active]:border-zinc-950 border border-transparent font-mono text-xs uppercase tracking-widest px-4 py-2">Diff</TabsTrigger>
              <TabsTrigger value="onboarding" data-testid="tab-onboarding" className="sharp data-[state=active]:bg-white data-[state=active]:border-zinc-950 border border-transparent font-mono text-xs uppercase tracking-widest px-4 py-2">Onboarding</TabsTrigger>
            </TabsList>

            <TabsContent value="overview" className="mt-6">
              <Section index="01" title="Business overview" testid="section-business">
                <BusinessOverview bp={a.business_profile} />
              </Section>
            </TabsContent>
            <TabsContent value="system" className="mt-6">
              <Section index="02" title="Inferred system map" testid="section-system">
                <SystemMap tp={a.tech_profile} />
              </Section>
            </TabsContent>
            <TabsContent value="features" className="mt-6">
              <Section index="03" title="Feature inventory" testid="section-features">
                <FeatureInventory fi={a.feature_inventory} />
              </Section>
            </TabsContent>
            <TabsContent value="lens" className="mt-6">
              <Section index="04" title="Industry lens applied" testid="section-lens">
                <IndustryLensView lens={a.selected_lens} />
              </Section>
            </TabsContent>
            <TabsContent value="competitors" className="mt-6">
              <Section index="05" title="Competitors" testid="section-competitors">
                <CompetitorsSection items={a.competitors} />
              </Section>
            </TabsContent>
            <TabsContent value="reviews" className="mt-6">
              <Section index="06" title="Public-review themes" testid="section-reviews">
                <ReviewsSection reviews={a.reviews} />
              </Section>
            </TabsContent>
            <TabsContent value="spec" className="mt-6">
              <Section index="07" title="Prioritised build spec (RICE-scored)" testid="section-spec">
                <BuildSpecView bs={a.build_spec} analysisId={a.id} competitors={a.competitors || []} onItemUpdated={() => qc.invalidateQueries({ queryKey: ["analysis", id] })} />
              </Section>
            </TabsContent>
            <TabsContent value="diff" className="mt-6">
              <Section index="08" title="Diff vs prior run" testid="section-diff">
                <DiffSection diff={a.diff} />
              </Section>
            </TabsContent>
            <TabsContent value="onboarding" className="mt-6">
              <Section index="09" title="Onboarding-flow walkthrough" testid="section-onboarding">
                <OnboardingTab a={a} onDone={() => qc.invalidateQueries({ queryKey: ["analysis", id] })} />
              </Section>
            </TabsContent>
          </Tabs>
        </div>

        <aside className="lg:sticky lg:top-6 self-start space-y-4">
          <ExportPanel id={id} />

          {a.diff && (
            <div className="border border-amber-300 bg-amber-50 p-4" data-testid="diff-callout">
              <div className="font-mono text-[10px] uppercase tracking-widest text-amber-800 mb-1">
                Diff vs prior run
              </div>
              <div className="text-sm text-amber-900 font-medium">{a.diff.summary}</div>
            </div>
          )}

          <ScreenshotStrip analysisId={a.id} pages={a.snapshot?.pages || []} />

          <div className="border border-zinc-200 bg-white">
            <div className="px-5 py-3 border-b border-zinc-200 font-mono text-[10px] uppercase tracking-widest text-zinc-500 bg-zinc-50">
              Run metadata
            </div>
            <div className="p-4 space-y-2 font-mono text-xs text-zinc-700">
              <div>id · {a.id.slice(0, 8)}</div>
              <div>created · {new Date(a.created_at).toLocaleString()}</div>
              <div>pages crawled · {a.snapshot?.pages?.length ?? 0}</div>
              <div>tech detected · {a.tech_profile?.items?.length ?? 0}</div>
              <div>features · {a.feature_inventory?.items?.length ?? 0}</div>
              <div>build items · {a.build_spec?.items?.length ?? 0}</div>
            </div>
          </div>

          {a.snapshot?.pages?.length > 0 && (
            <div className="border border-zinc-200 bg-white" data-testid="pages-crawled">
              <div className="px-5 py-3 border-b border-zinc-200 font-mono text-[10px] uppercase tracking-widest text-zinc-500 bg-zinc-50">
                Pages crawled
              </div>
              <div className="p-2 max-h-72 overflow-y-auto">
                {a.snapshot.pages.map((p, i) => (
                  <div key={i} className="px-3 py-1.5 hover:bg-zinc-50">
                    <div className="font-mono text-[10px] uppercase tracking-widest text-zinc-500">{p.page_type}</div>
                    <a href={p.url} target="_blank" rel="noreferrer" className="text-xs text-zinc-800 hover:underline break-all">{p.url}</a>
                  </div>
                ))}
              </div>
            </div>
          )}
        </aside>
      </div>
    </div>
  );
}
